import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  CopyButton,
  ProviderLogo,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { meetsRole } from '@/lib/space-roles';
import { routes } from '@/lib/routes';
import {
  useDeleteMcpServer,
  useMcpServer,
  useSyncMcpServer,
} from '@/hooks/useMcpServers';
import {
  useInvalidateMcpAuthStatus,
  useMcpAuthStatus,
} from '@/hooks/useMcpAuthStatus';
import { useMcpAuthEvents } from '@/hooks/useMcpAuthEvents';
import { useMcpProviderById } from '@/hooks/useMcpProviders';
import { useSpaceCredentials } from '@/hooks/useIdentityCredentials';
import { deriveMcpStatus } from '@/types/mcp';
import type { McpSyncResult } from '@/types';
import BackLink from '@/components/navigation/BackLink';
import { CimdClientBadge } from '@/pages/tools/mcp/CimdClientBadge';

export default function McpServerDetail() {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const { spaceId, role } = useSpaceContext();
  const isAdmin = meetsRole(role, 'admin');
  const { success, error: showError } = useToast();

  // AgentCore bakes this into the returned authorizationUrl so the popup
  // lands on our /oauth/connected page after consent. Whitelisted URLs:
  //   http://localhost:5173/oauth/connected
  //   https://operator.stg.optr.ai/oauth/connected
  //   https://builder.stg.optr.ai/oauth/connected   (legacy — drop with Phase 2)
  const returnUrl = `${window.location.origin}/oauth/connected`;

  const { data: server, isLoading } = useMcpServer(serverId);
  const catalogEntry = useMcpProviderById(server?.providerId);
  const { data: auth, refetch: refetchAuth } = useMcpAuthStatus(serverId, returnUrl);
  // The OAuth app lives in optr-identity (a papp row keyed by serverId), not on
  // the agent-core server config — so the credentials response is the only
  // place that knows which client is wired up.
  const { data: credentials } = useSpaceCredentials(spaceId);
  const oauthApp = credentials?.providers.find(
    (p) => p.providerInstanceId === serverId,
  );
  const invalidateAuth = useInvalidateMcpAuthStatus();
  const syncServer = useSyncMcpServer();
  const deleteServer = useDeleteMcpServer();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<McpSyncResult | null>(null);

  // Listen for the backend "connected" event so the auth-status card flips
  // automatically when the user finishes consent in the popup tab.
  useMcpAuthEvents({
    serverId,
    onConnected: () => {
      success('Connected to MCP server');
    },
    onFailed: (_id, message) => {
      showError(message ?? 'Connection failed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!server) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        MCP server not found.{' '}
        <Link
          to={routes.console.spaceToolsMcpServers(spaceId)}
          className="text-primary underline"
        >
          Back to MCP servers
        </Link>
      </div>
    );
  }

  // Stash the (spaceId, serverId) that's about to be authorized so the
  // /oauth/connected landing page can name the server in its success copy.
  // localStorage is shared across same-origin tabs even when the popup is
  // opened with noopener (sessionStorage isn't). The landing page consumes
  // and clears the value on mount.
  const openAuthorizationUrl = (url: string) => {
    try {
      localStorage.setItem(
        'optr.oauth.pending',
        JSON.stringify({
          spaceId,
          serverId: server.serverId,
          ts: Date.now(),
        }),
      );
    } catch {
      // localStorage disabled (private browsing on some browsers) — landing
      // page will fall back to the generic "Connected" message.
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSync = async () => {
    setSyncResult(null);
    try {
      const result = await syncServer.mutateAsync({
        serverId: server.serverId,
        returnUrl,
      });
      setSyncResult(result);
      if (result.authorizationRequired && result.authorizationUrl) {
        openAuthorizationUrl(result.authorizationUrl);
        // The auth-status query will be refreshed by the WS event when the
        // user finishes consent — no manual polling needed.
        return;
      }
      success(
        `Sync complete — ${result.imported} imported, ${result.updated} updated, ${result.removed} removed`,
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Sync failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteServer.mutateAsync(server.serverId);
      success('MCP server deleted');
      navigate(routes.console.spaceToolsMcpServers(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete server');
    }
  };

  const handleConnect = () => {
    if (auth?.authorizationUrl) openAuthorizationUrl(auth.authorizationUrl);
  };

  const handleCheckAgain = () => {
    if (server) {
      invalidateAuth(server.serverId);
      refetchAuth();
    }
  };

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceToolsMcpServers(spaceId)}>MCP Servers</BackLink>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {server.providerId && (
            <ProviderLogo
              slug={server.providerId}
              displayName={catalogEntry?.displayName}
              className="h-12 w-12"
              fallbackClassName="text-sm"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">{server.name}</h1>
            {catalogEntry && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {catalogEntry.displayName}
              </p>
            )}
          </div>
        </div>
        {(() => {
          const status = deriveMcpStatus(server);
          return (
            <Badge variant={getStatusVariant(status)}>{status}</Badge>
          );
        })()}
      </div>

      {/* Callback URL — only meaningful for OAuth installs that actually
          do the redirect dance. api-key and oauth_manual skip it; the
          backend may also return an empty string when there's no callback
          to share. */}
      {server.authMode !== 'api_key' &&
        server.authMode !== 'oauth_manual' &&
        server.callbackUrl.trim().length > 0 && (
          <Card>
            <CardHeader
              title="Callback URL"
              description="If you supplied your own OAuth client, add this as an allowed redirect URI on your OAuth app. (Servers that support Dynamic Client Registration don't need this.)"
            />
            <div className="flex items-center gap-3">
              <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
                {server.callbackUrl}
              </code>
              <CopyButton value={server.callbackUrl} label="Copy" size="md" />
            </div>
          </Card>
        )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Configuration */}
        <Card>
          <CardHeader
            title="Configuration"
            action={
              isAdmin ? (
                <Link
                  to={`${routes.console.spaceToolsMcpServer(
                    spaceId,
                    server.serverId,
                  )}/edit`}
                >
                  <Button variant="outline" size="sm">
                    Edit
                  </Button>
                </Link>
              ) : undefined
            }
          />
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server URL
              </dt>
              <dd className="mt-0.5 text-sm text-foreground break-all">
                {server.serverUrl}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Default group
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.defaultGroup || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Scopes
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.scopes && server.scopes.length > 0 ? (
                  <span className="flex flex-wrap gap-1">
                    {server.scopes.map((scope) => (
                      <Badge key={scope} variant="gray">
                        {scope}
                      </Badge>
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                OAuth client
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {oauthApp?.oauthApp ? (
                  <>
                    <span className="font-mono text-xs">
                      {oauthApp.oauthApp.clientId}
                    </span>
                    {!oauthApp.oauthApp.hasClientSecret && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        public client — no secret
                      </span>
                    )}
                  </>
                ) : server.oauthClientMode === 'cimd' ? (
                  <CimdClientBadge />
                ) : (
                  'Not configured'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server ID
              </dt>
              <dd className="mt-0.5 text-sm text-foreground font-mono text-xs">
                {server.serverId}
              </dd>
            </div>
            {server.lastError && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Last error
                </dt>
                <dd className="mt-0.5 text-sm text-red-600 dark:text-red-400">
                  {server.lastError}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Per-user auth status */}
        <Card>
          <CardHeader
            title="Your connection"
            description="Each space member connects for themselves. Agents you own use your connection at runtime."
          />
          {auth?.authorized ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Connected</span>
              </div>
              {auth.scopes && auth.scopes.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
                  <span>Scopes:</span>
                  {auth.scopes.map((scope) => (
                    <Badge key={scope} variant="gray">
                      {scope}
                    </Badge>
                  ))}
                </div>
              )}
              {auth.expiresAt && (
                <p className="text-xs text-muted-foreground">
                  Renews automatically • token expires{' '}
                  {new Date(auth.expiresAt).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {auth?.lastError && (
                <div className="flex gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{auth.lastError}</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Connect to grant agents you own access to this MCP server. The
                authorization page opens in a new tab.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleConnect}
                  disabled={!auth?.authorizationUrl}
                >
                  Connect {server.name}
                  <ExternalLink className="h-3.5 w-3.5 ml-2" />
                </Button>
                <Button variant="outline" onClick={handleCheckAgain}>
                  Check again
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Sync */}
      <Card>
        <CardHeader
          title="Tools"
          description="Pull tools from the MCP server into this space's tool catalog."
          action={
            isAdmin ? (
              <Button onClick={handleSync} disabled={syncServer.isPending}>
                {syncServer.isPending ? 'Syncing…' : 'Sync tools'}
              </Button>
            ) : undefined
          }
        />
        {syncResult ? (
          syncResult.authorizationRequired ? (
            <div className="rounded-md p-3 text-sm bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300">
              <p className="font-medium">Authorization required</p>
              <p className="mt-1">
                We opened the consent page in a new tab. Finish connecting and
                the sync will succeed on the next attempt.
              </p>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-foreground">
                {syncResult.imported} imported · {syncResult.updated} updated ·{' '}
                {syncResult.skipped} skipped · {syncResult.removed} removed
              </p>
              {syncResult.errors.length > 0 && (
                <ul className="text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                  {syncResult.errors.map((err, i) => (
                    <li key={i}>
                      {err.tool ? `${err.tool}: ` : ''}
                      {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        ) : server.lastSyncedAt ? (
          <p className="text-sm text-muted-foreground">
            Last synced {new Date(server.lastSyncedAt).toLocaleString()}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Not synced yet.</p>
        )}
      </Card>

      {/* Danger Zone */}
      {isAdmin && (
        <Card>
          <CardHeader title="Danger Zone" description="Irreversible actions" />
          <Button
            variant="destructive"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete MCP Server
          </Button>
        </Card>
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete MCP Server"
        message={`Are you sure you want to delete "${server.name}"? This removes its tools from this space and revokes all per-user connections.`}
        confirmText="Delete"
        isLoading={deleteServer.isPending}
      />
    </div>
  );
}
